import type { SymbolCacheEntry } from '../reducers/ui';
import type { ApplicationMenuType } from '../types';
import type { EnqueueToastAction, DismissAllToastsAction } from './toast';
import { DEFAULT_GROUP_CONFIG, DEFAULT_TOAST_CONFIG } from '@thinkalpha/ui-types/types/toast';
import type { ToastGroup, ToastVariant } from '@thinkalpha/ui-types/types/toast';

/**
 * Toast message type - string only for serializable Redux state
 * @deprecated Import ToastOptions from 'src/types/toast' instead
 */
export type SnackbarMessage = string;

/**
 * Options for snackbar/toast display
 * @deprecated Import ToastOptions from 'src/types/toast' instead
 */
export interface SnackbarOptions {
    /** Visual variant */
    variant?: 'default' | 'success' | 'error' | 'warning' | 'info';
    /** Auto-dismiss duration in ms */
    autoHideDuration?: number;
    /** If true, toast persists until manually dismissed */
    persist?: boolean;
    /** Optional key for deduplication */
    key?: string | number;
    /** Toast group for grouping behavior */
    group?: ToastGroup;
    /** For error toasts - enables copy-to-clipboard UI */
    requestId?: string;
    /** For developer mode - shown below requestId */
    debugMessage?: string;
}

/** @deprecated Use EnqueueToastAction from toast actions */
export type SetSnackbarAction = EnqueueToastAction;

/**
 * Primary API for showing toast notifications.
 * @deprecated Use useToast hook or direct dispatch with EnqueueToastAction
 *
 * @example
 * dispatch(setSnackbar('Success!', { variant: 'success' }));
 * dispatch(setSnackbar('Alert triggered', { variant: 'info', persist: true, group: 'alerts' }));
 */
export const setSnackbar = (message: SnackbarMessage, options?: SnackbarOptions): EnqueueToastAction => {
    const variant: ToastVariant = options?.variant ?? 'info';
    const group = options?.group ?? 'default';
    const groupConfig = DEFAULT_TOAST_CONFIG.groups[group] ?? DEFAULT_GROUP_CONFIG;

    // Determine if toast should persist
    const shouldPersist =
        options?.persist !== undefined
            ? options.persist
            : options?.autoHideDuration !== undefined
              ? false
              : (groupConfig.persist ?? false);

    // Determine autoHideDuration
    const autoHideDuration = shouldPersist
        ? null
        : (options?.autoHideDuration ?? groupConfig.defaultDuration ?? DEFAULT_TOAST_CONFIG.defaultAutoHideDuration);

    return {
        type: 'toast::enqueue',
        toast: {
            // Only set id if key is provided (for deduplication), otherwise let reducer auto-generate
            id: options?.key?.toString(),
            message,
            // Only include variant if not 'info' (the default)
            ...(variant !== 'info' && { variant }),
            group,
            autoHideDuration,
            requestId: options?.requestId,
            debugMessage: options?.debugMessage,
        },
    };
};

export type SetSymbolCacheAction = { type: 'symbol-cache::set'; symbolCache: SymbolCacheEntry[] };
export const setSymbolCache = (symbolCache: SymbolCacheEntry[]): SetSymbolCacheAction => ({
    type: 'symbol-cache::set',
    symbolCache,
});

export type AddToSymbolCacheAction = { type: 'symbol-cache::add'; symbol: string };
export const addToSymbolCache = (symbol: string): AddToSymbolCacheAction => ({
    type: 'symbol-cache::add',
    symbol,
});

export type RemoveFromSymbolCacheAction = { type: 'symbol-cache::remove'; symbol: string };
export const removeFromSymbolCache = (symbol: string): RemoveFromSymbolCacheAction => ({
    type: 'symbol-cache::remove',
    symbol,
});

/** @deprecated Use DismissAllToastsAction from toast actions */
export type ClearSnackbarAction = DismissAllToastsAction;

/**
 * Clears all toasts. Kept for backward compatibility.
 */
export const clearSnackbar = (): DismissAllToastsAction => ({ type: 'toast::dismiss-all' });

export const setApplicationMenu = (menu: ApplicationMenuType) => ({
    type: 'menu::set-application-menu' as const,
    menu,
});

export type SetApplicationMenuAction = ReturnType<typeof setApplicationMenu>;

export type SetNewsTicker = { type: 'news::setTicker'; ticker: string };

export const setNewsTicker = (ticker: string): SetNewsTicker => ({
    type: 'news::setTicker',
    ticker,
});

export type SetWidgetsBlocked = { type: 'widgets::blocked'; blocked: boolean };
export const setWidgetsBlocked = (blocked: boolean): SetWidgetsBlocked => ({
    type: 'widgets::blocked',
    blocked,
});

export type UIAction =
    | SetSnackbarAction
    | ClearSnackbarAction
    | SetApplicationMenuAction
    | SetSymbolCacheAction
    | AddToSymbolCacheAction
    | RemoveFromSymbolCacheAction
    | SetWidgetsBlocked;
